import type { OnDestroy } from '@angular/core';
import { inject, Injectable, signal } from '@angular/core';

import type { VirtualScrollerComponent } from '@iharbeck/ngx-virtual-scroller';
import { BehaviorSubject, Subject } from 'rxjs';

import { SelectItemStrategy } from '../strategies/select-item/select-item-strategy';
import type { ISelectController, Select2Item, SelectContext, SelectItemType } from '../types';
import { ItemSelector } from '../types';

@Injectable()
export class SelectController<
  Select extends SelectContext = { items: Select2Item[] },
  IS extends ItemSelector<any> = SelectItemStrategy<SelectItemType<Select>>,
>
  implements OnDestroy, ISelectController<Select, IS>
{
  public itemSelector =
    <IS>inject(ItemSelector, { optional: true }) ??
    new SelectItemStrategy<SelectItemType<Select>>();

  public isDisabled = signal<boolean>(false);
  public isOpen = signal<boolean>(false);
  public virtualScroll = signal<VirtualScrollerComponent | undefined>(undefined);
  public clear$ = new Subject<void>();
  public select$ = new Subject<Select2Item | Select2Item[]>();
  public keyDown$ = new Subject<KeyboardEvent>();
  public navigation$ = new BehaviorSubject<{ type: 'item'; index: number; visible: boolean }>({
    type: 'item',
    index: 0,
    visible: false,
  });
  public focusTrigger$ = new Subject<void>();
  public detectChanges$ = new Subject<void>();
  public ngOnDestroy$ = new Subject<void>();

  public setDisabled(value: boolean) {
    this.isDisabled.set(value);
  }

  public ngOnDestroy() {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public toggle(value?: boolean) {
    if (this.isDisabled()) return;

    const nextValue = value ?? !this.isOpen();
    this.isOpen.set(nextValue);
  }

  public scrollToIndex(index: number) {
    const virtualScroll = this.virtualScroll();
    if (!virtualScroll) return;

    virtualScroll.scrollToIndex(index, true, -30);
  }

  public isHovered(itemIndex: number) {
    const { index, visible } = this.navigation$.value;
    return visible && index === itemIndex;
  }
}
